import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_ALLOWED_TASK_KINDS,
  DENIED_TASK_KINDS,
  ROUTE_JUDGE_PROMPT,
  ROUTE_JUDGE_PROMPT_VERSION,
  resolveAutoRoutingEffective,
} from '../../../../shared-types/src/index.js';
import { DataTable } from '../../components/DataTable.js';
import { Metric } from '../../components/Metric.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type {
  AutoRoutingPreviewRequest,
  AutoRoutingPreviewResult,
  AutoRoutingDecisionsView,
  AutoRoutingProfileView,
  AutoRoutingProfileWrite,
  ModelTier,
  ToastMessage,
} from '../../types.js';

type AvailableCandidate = AutoRoutingProfileView['availableCandidates'][number];
type DecisionEvent = AutoRoutingDecisionsView['events'][number];

export function judgeCandidateDisabledReason(candidate: AvailableCandidate): string | undefined {
  if (candidate.status !== 'active' || !candidate.routeActive) return 'inactive';
  if (!candidate.capabilities.structuredOutput) return 'structuredOutput';
  if (!candidate.rateAvailable) return 'rateMissing';
  return undefined;
}

export function summarizeRouteDecisions(events: DecisionEvent[]) {
  const tiers = { weak: 0, medium: 0, strong: 0, local: 0 };
  const reasons: Record<string, number> = {};
  let judgeOk = 0;
  let judgeRuns = 0;
  let cacheHits = 0;
  for (const event of events) {
    const decision = event.routeDecision;
    if (!decision) continue;
    tiers[decision.selectedTier] += 1;
    reasons[decision.decisionReason] = (reasons[decision.decisionReason] ?? 0) + 1;
    if (decision.judge) {
      judgeRuns += 1;
      if (decision.judge.outcome === 'ok') judgeOk += 1;
      if (decision.judge.cacheHit) cacheHits += 1;
    }
  }
  const topReasons = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
  return { tiers, topReasons, judgeRuns, judgeOk, cacheHits };
}

const ALL_TASK_KINDS: NonNullable<AutoRoutingPreviewRequest['taskKind']>[] = [
  ...DEFAULT_ALLOWED_TASK_KINDS,
  ...DENIED_TASK_KINDS,
];

const EMPTY_DRAFT: AutoRoutingProfileWrite = {
  candidates: [],
  objective: 'cost',
  providerMode: 'single-provider',
  deploymentMode: 'shadow',
  enabled: false,
};

export function setTierCandidate(
  candidates: AutoRoutingProfileWrite['candidates'],
  tier: ModelTier,
  catalogModelId: string
): AutoRoutingProfileWrite['candidates'] {
  const remaining = candidates.filter(
    (candidate) => candidate.tier !== tier && candidate.catalogModelId !== catalogModelId
  );
  return catalogModelId ? [...remaining, { tier, catalogModelId }] : remaining;
}

export function validateAutoRoutingDraft(
  draft: AutoRoutingProfileWrite,
  available: AvailableCandidate[]
): string[] {
  const errors: string[] = [];
  const count = (tier: ModelTier) =>
    draft.candidates.filter((candidate) => candidate.tier === tier).length;
  if (count('weak') !== 1) errors.push('weak_required');
  if (count('strong') !== 1) errors.push('strong_required');
  if (count('medium') > 1) errors.push('medium_cardinality');
  if (
    new Set(draft.candidates.map((candidate) => candidate.catalogModelId)).size !==
    draft.candidates.length
  )
    errors.push('candidate_duplicate');
  const byId = new Map(available.map((candidate) => [candidate.catalogModelId, candidate]));
  const selected = draft.candidates.map((candidate) => byId.get(candidate.catalogModelId));
  if (selected.some((candidate) => !candidate)) errors.push('candidate_missing');
  if (selected.some((candidate) => candidate && !candidate.thinkingAuto))
    errors.push('thinking_auto_unsupported');
  if (
    selected.some(
      (candidate) => candidate && (candidate.status !== 'active' || !candidate.routeActive)
    )
  )
    errors.push('candidate_inactive');
  if (
    draft.providerMode === 'single-provider' &&
    new Set(selected.flatMap((candidate) => (candidate ? [candidate.provider] : []))).size > 1
  )
    errors.push('provider_mismatch');
  if (new Set(selected.flatMap((candidate) => (candidate ? [candidate.billingMode] : []))).size > 1)
    errors.push('billing_mode_mismatch');
  if (
    draft.deploymentMode === 'runtime' &&
    selected.some((candidate) => candidate && !candidate.rateAvailable)
  )
    errors.push('rate_missing');
  if (
    draft.thresholdOverride !== undefined &&
    (!Number.isFinite(draft.thresholdOverride) ||
      draft.thresholdOverride < 0 ||
      draft.thresholdOverride > 1)
  )
    errors.push('threshold_range');
  if (
    (draft.allowedTaskKinds as unknown[] | undefined)?.some((taskKind) =>
      (DENIED_TASK_KINDS as readonly unknown[]).includes(taskKind)
    )
  )
    errors.push('task_kind_denied');
  const judgeCandidate = draft.judge?.catalogModelId
    ? byId.get(draft.judge.catalogModelId)
    : undefined;
  if (
    draft.judge?.mode !== 'off' &&
    draft.judge?.catalogModelId &&
    (!judgeCandidate || judgeCandidateDisabledReason(judgeCandidate))
  ) errors.push('judge_model_invalid');
  return errors;
}

export function criteriaSummaryOf(draft: AutoRoutingProfileWrite) {
  return resolveAutoRoutingEffective(draft);
}

function draftFromProfile(profile: AutoRoutingProfileView): AutoRoutingProfileWrite {
  const auto = profile.catalogItem?.routing.auto;
  if (!auto) return { ...EMPTY_DRAFT, candidates: [] };
  return {
    candidates: profile.catalogItem!.routing.candidates.flatMap((candidate) =>
      candidate.catalogModelId
        ? [{ tier: candidate.tier, catalogModelId: candidate.catalogModelId }]
        : []
    ),
    objective: auto.objective === 'quality' ? 'quality' : 'cost',
    providerMode: auto.providerMode,
    deploymentMode: auto.deploymentMode,
    enabled: auto.enabled,
    ...(auto.thresholdOverride !== undefined
      ? { thresholdOverride: auto.thresholdOverride }
      : {}),
    ...(auto.allowedTaskKinds ? { allowedTaskKinds: [...auto.allowedTaskKinds] } : {}),
    ...(auto.judge ? { judge: { ...auto.judge } } : {}),
  };
}

function selectedId(draft: AutoRoutingProfileWrite, tier: ModelTier): string {
  return draft.candidates.find((candidate) => candidate.tier === tier)?.catalogModelId ?? '';
}

export function AutoRoutingTab({
  http,
  tenantId,
  onToast,
}: {
  http: AdminHttpClient;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<AutoRoutingProfileView>();
  const [draft, setDraft] = useState<AutoRoutingProfileWrite>(EMPTY_DRAFT);
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [taskKind, setTaskKind] = useState<AutoRoutingPreviewRequest['taskKind']>();
  const [risk, setRisk] = useState<'low' | 'medium' | 'high'>('low');
  const [finalAnswer, setFinalAnswer] = useState(false);
  const [compliance, setCompliance] = useState(false);
  const [preview, setPreview] = useState<AutoRoutingPreviewResult>();
  const [decisions, setDecisions] = useState<DecisionEvent[]>([]);
  const requestSequence = useRef(0);
  const decisionSequence = useRef(0);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    setPreview(undefined);
    if (!tenantId) {
      setProfile(undefined);
      setDraft({ ...EMPTY_DRAFT, candidates: [] });
      return;
    }
    setBusy(true);
    void http
      .getAutoRoutingProfile(tenantId)
      .then((next) => {
        if (sequence !== requestSequence.current) return;
        const nextDraft = draftFromProfile(next);
        setProfile(next);
        setDraft(nextDraft);
        const selectedProvider = nextDraft.candidates
          .map(
            (candidate) =>
              next.availableCandidates.find(
                (model) => model.catalogModelId === candidate.catalogModelId
              )?.provider
          )
          .find(Boolean);
        setProvider(selectedProvider ?? next.availableCandidates[0]?.provider ?? '');
      })
      .catch((error) => {
        if (sequence === requestSequence.current)
          onToast({ tone: 'error', message: describeError(error, t) });
      })
      .finally(() => {
        if (sequence === requestSequence.current) setBusy(false);
      });
  }, [http, onToast, t, tenantId]);

  useEffect(() => {
    const sequence = ++decisionSequence.current;
    if (!tenantId) {
      setDecisions([]);
      return;
    }
    void http.listAutoRoutingDecisions(tenantId, 200)
      .then((view) => {
        if (sequence === decisionSequence.current) setDecisions(view.events);
      })
      .catch((error) => {
        if (sequence === decisionSequence.current) {
          onToast({ tone: 'error', message: describeError(error, t) });
        }
      });
  }, [http, onToast, t, tenantId]);

  const available = useMemo(
    () => profile?.availableCandidates ?? [],
    [profile?.availableCandidates]
  );
  const providers = [...new Set(available.map((candidate) => candidate.provider))].sort();
  const visibleCandidates = available.filter((candidate) => {
    if (draft.providerMode === 'single-provider' && provider && candidate.provider !== provider)
      return false;
    const needle = search.trim().toLowerCase();
    return (
      !needle ||
      candidate.displayName.toLowerCase().includes(needle) ||
      candidate.catalogModelId.toLowerCase().includes(needle)
    );
  });
  const validation = validateAutoRoutingDraft(draft, available);
  const criteria = criteriaSummaryOf(draft);
  const decisionSummary = summarizeRouteDecisions(decisions);
  const selected = useMemo(
    () =>
      draft.candidates.flatMap((candidate) => {
        const model = available.find((item) => item.catalogModelId === candidate.catalogModelId);
        return model ? [{ ...candidate, model }] : [];
      }),
    [available, draft.candidates]
  );
  const sharedCapabilities = selected.length
    ? Object.entries(selected[0].model.capabilities)
        .filter(
          ([key, value]) =>
            typeof value === 'boolean' &&
            value &&
            selected.every(
              ({ model }) => model.capabilities[key as keyof typeof model.capabilities] === true
            )
        )
        .map(([key]) => key)
    : [];
  const minContext = selected.length
    ? Math.min(...selected.map(({ model }) => model.limits.contextWindow))
    : undefined;
  const minOutput = selected.length
    ? Math.min(...selected.map(({ model }) => model.limits.maxOutputTokens))
    : undefined;
  const status =
    profile?.status === 'degraded'
      ? 'degraded'
      : !draft.enabled
        ? 'disabled'
        : draft.deploymentMode;

  function changeProvider(nextProvider: string) {
    setProvider(nextProvider);
    setDraft((current) => ({
      ...current,
      candidates: current.candidates.filter(
        (candidate) =>
          available.find((model) => model.catalogModelId === candidate.catalogModelId)?.provider ===
          nextProvider
      ),
    }));
  }

  async function save(nextDraft = draft, allowInvalidDisable = false) {
    if (
      !tenantId ||
      (!allowInvalidDisable && validateAutoRoutingDraft(nextDraft, available).length > 0)
    )
      return;
    if (
      nextDraft.enabled &&
      nextDraft.deploymentMode === 'runtime' &&
      !window.confirm(t('autoRouting.runtimeConfirm'))
    )
      return;
    const sequence = ++requestSequence.current;
    setBusy(true);
    try {
      const next = await http.putAutoRoutingProfile(tenantId, nextDraft);
      if (sequence !== requestSequence.current) return;
      setProfile(next);
      setDraft(draftFromProfile(next));
      onToast({ tone: 'success', message: t('autoRouting.saved') });
    } catch (error) {
      if (sequence === requestSequence.current)
        onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }

  async function runPreview(mode: 'local' | 'judge' = 'local') {
    if (!tenantId) return;
    if (mode === 'judge' && !window.confirm(t('autoRouting.judgePreview.confirm'))) return;
    const sequence = ++requestSequence.current;
    setBusy(true);
    try {
      const next = await http.previewAutoRouting(tenantId, {
        mode,
        prompt,
        taskKind,
        risk,
        finalAnswer,
        compliance,
      });
      if (sequence === requestSequence.current) setPreview(next);
    } catch (error) {
      if (sequence === requestSequence.current)
        onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }

  if (!tenantId) return <section className="panel">{t('autoRouting.selectTenant')}</section>;

  return (
    <div className="content-stack">
      <section className="panel form-grid">
        <h2>{t('autoRouting.title')}</h2>
        <p>
          <strong>{t(`autoRouting.status.${status}`)}</strong>
          {profile?.updatedBy
            ? ` · ${t('autoRouting.lastChanged', {
                actor: profile.updatedBy,
                time: profile.updatedAt ? new Date(profile.updatedAt).toLocaleString() : '-',
              })}`
            : null}
        </p>
        {profile?.reasonCodes.length ? (
          <div className="notice error">
            {profile.reasonCodes.map((reason) => (
              <div key={reason}>{t(`autoRouting.reason.${reason}`, reason)}</div>
            ))}
          </div>
        ) : null}
        <label>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) =>
              setDraft((current) => ({ ...current, enabled: event.target.checked }))
            }
          />
          {t('autoRouting.enabled')}
        </label>
      </section>

      <section className="panel form-grid">
        <h2>{t('autoRouting.configuration')}</h2>
        <label>
          {t('autoRouting.providerMode')}
          <select
            value={draft.providerMode}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                providerMode: event.target.value as AutoRoutingProfileWrite['providerMode'],
              }))
            }
          >
            <option value="single-provider">{t('autoRouting.singleProvider')}</option>
            <option value="mixed-provider">{t('autoRouting.mixedProvider')}</option>
          </select>
        </label>
        {draft.providerMode === 'single-provider' ? (
          <label>
            {t('autoRouting.provider')}
            <select
              value={provider}
              required
              onChange={(event) => changeProvider(event.target.value)}
            >
              <option value="">{t('common.none')}</option>
              {providers.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          {t('common.search')}
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('autoRouting.searchPlaceholder')}
          />
        </label>
        {(['weak', 'medium', 'strong'] as const).map((tier) => (
          <label key={tier}>
            {t(`autoRouting.tier.${tier}`)}
            <select
              value={selectedId(draft, tier)}
              required={tier !== 'medium'}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  candidates: setTierCandidate(current.candidates, tier, event.target.value),
                }))
              }
            >
              <option value="">{t('common.none')}</option>
              {visibleCandidates.map((candidate) => (
                <option
                  key={candidate.catalogModelId}
                  value={candidate.catalogModelId}
                  disabled={draft.candidates.some(
                    (item) => item.catalogModelId === candidate.catalogModelId && item.tier !== tier
                  ) || !candidate.thinkingAuto}
                >
                  {candidate.displayName} · {candidate.provider} · {candidate.catalogModelId}
                  {` · Auto:${candidate.capabilities.thinkingAuto?.strategy ?? 'unsupported'}`}
                  {` · ${candidate.limits.contextWindow}/${candidate.limits.maxOutputTokens}`}
                  {candidate.billingRate
                    ? ` · $${candidate.billingRate.inputUsdPerMillion}/$${candidate.billingRate.outputUsdPerMillion}/1M ×${candidate.billingRate.marginMultiplier}`
                    : ` · ${candidate.billingMode}`}
                  {!candidate.rateAvailable ? ` · ${t('autoRouting.rateMissing')}` : ''}
                </option>
              ))}
            </select>
          </label>
        ))}
        <label>
          {t('autoRouting.objective')}
          <select
            value={draft.objective}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                objective: event.target.value as AutoRoutingProfileWrite['objective'],
              }))
            }
          >
            <option value="cost">{t('autoRouting.objectiveCost')}</option>
            <option value="quality">{t('autoRouting.objectiveQuality')}</option>
          </select>
        </label>
        <label>
          {t('autoRouting.deploymentMode')}
          <select
            value={draft.deploymentMode}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                deploymentMode: event.target.value as AutoRoutingProfileWrite['deploymentMode'],
              }))
            }
          >
            <option value="shadow">{t('autoRouting.shadowRecommended')}</option>
            <option value="runtime">{t('autoRouting.runtime')}</option>
          </select>
        </label>
        {validation.length ? (
          <div className="notice error">
            {validation.map((error) => (
              <div key={error}>{t(`autoRouting.validation.${error}`, error)}</div>
            ))}
          </div>
        ) : null}
        <p>
          {t('autoRouting.intersection', {
            capabilities: sharedCapabilities.join(', ') || '-',
            contextTokens: minContext ?? '-',
            output: minOutput ?? '-',
          })}
        </p>
        <div>
          <button disabled={busy || validation.length > 0} onClick={() => void save()}>
            {t('common.save')}
          </button>{' '}
          <button
            disabled={busy || !profile?.catalogItem}
            onClick={() => void save({ ...draft, enabled: false, deploymentMode: 'shadow' }, true)}
          >
            {t('autoRouting.disable')}
          </button>
        </div>
      </section>

      <section className="panel form-grid auto-routing-criteria">
        <h2>{t('autoRouting.criteria.title')}</h2>
        <dl className="criteria-summary">
          <div>
            <dt>{t('autoRouting.criteria.effectiveThreshold')}</dt>
            <dd>
              {criteria.threshold.toFixed(2)} ·{' '}
              {t(`autoRouting.criteria.thresholdSource.${criteria.thresholdSource}`)}
            </dd>
          </div>
          <div>
            <dt>{t('autoRouting.criteria.mediumBand')}</dt>
            <dd>
              {criteria.mediumBandThreshold.toFixed(2)} ≤ score &lt;{' '}
              {criteria.threshold.toFixed(2)}
            </dd>
          </div>
        </dl>
        <div>
          <strong>{t('autoRouting.criteria.allowedKinds')}</strong>
          <div className="badge-list">
            {criteria.allowedTaskKinds.map((kind) => (
              <span className="badge active" key={kind}>
                {t(`autoRouting.taskKindLabel.${kind}`)}
              </span>
            ))}
          </div>
        </div>
        <div>
          <strong>{t('autoRouting.criteria.deniedKinds')}</strong>
          <div className="badge-list">
            {DENIED_TASK_KINDS.map((kind) => (
              <span className="badge disabled" key={kind}>
                {t(`autoRouting.taskKindLabel.${kind}`)}
              </span>
            ))}
          </div>
          <p className="muted">{t('autoRouting.criteria.deniedLocked')}</p>
        </div>
        <p className="muted">{t('autoRouting.criteria.direction')}</p>
        {criteria.allowedTaskKinds.length === 0 ? (
          <p className="warning">{t('autoRouting.criteria.emptyKindsWarning')}</p>
        ) : null}
        <details>
          <summary>{t('autoRouting.criteria.advanced')}</summary>
          <div className="form-grid criteria-advanced">
            <label>
              {t('autoRouting.criteria.overrideLabel')}
              <div className="range-number-pair">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={draft.thresholdOverride ?? criteria.threshold}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      thresholdOverride: Number(event.target.value),
                    }))
                  }
                />
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={draft.thresholdOverride ?? ''}
                  placeholder={criteria.threshold.toFixed(2)}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      thresholdOverride:
                        event.target.value === '' ? undefined : Number(event.target.value),
                    }))
                  }
                />
              </div>
            </label>
            <button
              type="button"
              disabled={draft.thresholdOverride === undefined}
              onClick={() =>
                setDraft((current) => ({ ...current, thresholdOverride: undefined }))
              }
            >
              {t('autoRouting.criteria.overrideReset')}
            </button>
            <div>
              <strong>{t('autoRouting.criteria.allowedKinds')}</strong>
              {DEFAULT_ALLOWED_TASK_KINDS.map((kind) => (
                <label className="check-row" key={kind}>
                  <input
                    type="checkbox"
                    checked={criteria.allowedTaskKinds.includes(kind)}
                    onChange={(event) =>
                      setDraft((current) => {
                        const currentKinds = criteriaSummaryOf(current).allowedTaskKinds;
                        return {
                          ...current,
                          allowedTaskKinds: event.target.checked
                            ? [...currentKinds, kind]
                            : currentKinds.filter((item) => item !== kind),
                        };
                      })
                    }
                  />
                  <span>{t(`autoRouting.taskKindLabel.${kind}`)}</span>
                </label>
              ))}
              {DENIED_TASK_KINDS.map((kind) => (
                <label className="check-row locked" key={kind}>
                  <input type="checkbox" checked={false} disabled />
                  <span>{t(`autoRouting.taskKindLabel.${kind}`)}</span>
                </label>
              ))}
            </div>
            <div className="form-grid">
              <strong>{t('autoRouting.judge.title')}</strong>
              <label>
                {t('autoRouting.judge.model')}
                <select
                  value={draft.judge?.catalogModelId ?? ''}
                  disabled={draft.judge?.mode === 'off' || !draft.judge}
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    judge: {
                      mode: current.judge?.mode ?? 'shadow',
                      ...(event.target.value ? { catalogModelId: event.target.value } : {}),
                    },
                  }))}
                >
                  <option value="">
                    {t('autoRouting.judge.modelDefault', {
                      model: selectedId(draft, 'weak') || t('common.none'),
                    })}
                  </option>
                  {available.map((candidate) => {
                    const reason = judgeCandidateDisabledReason(candidate);
                    return (
                      <option
                        key={candidate.catalogModelId}
                        value={candidate.catalogModelId}
                        disabled={Boolean(reason)}
                      >
                        {candidate.displayName} · {candidate.catalogModelId}
                        {reason ? ` · ${t(`autoRouting.judge.disabledReason.${reason}`)}` : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
              <div className="segmented-control three-way">
                {(['off', 'shadow', 'runtime'] as const).map((mode) => (
                  <button
                    type="button"
                    className={(draft.judge?.mode ?? 'off') === mode ? 'active' : undefined}
                    key={mode}
                    onClick={() => {
                      if (
                        mode === 'runtime' &&
                        draft.judge?.mode !== 'runtime' &&
                        !window.confirm(t('autoRouting.judge.runtimeConfirm'))
                      ) return;
                      setDraft((current) => ({
                        ...current,
                        judge: { mode, catalogModelId: current.judge?.catalogModelId },
                      }));
                    }}
                  >
                    {t(`autoRouting.judge.mode.${mode}`)}
                  </button>
                ))}
              </div>
              <p className="muted">{t('autoRouting.judge.shadowCostNotice')}</p>
              <details>
                <summary>{t('autoRouting.judge.promptTitle')}</summary>
                <p>{t('autoRouting.judge.promptVersion', { version: ROUTE_JUDGE_PROMPT_VERSION })}</p>
                <pre>{ROUTE_JUDGE_PROMPT}</pre>
              </details>
            </div>
          </div>
        </details>
      </section>

      <section className="panel form-grid">
        <div className="panel-heading">
          <h2>{t('autoRouting.decisions.title')}</h2>
          <button
            type="button"
            onClick={() => {
              const sequence = ++decisionSequence.current;
              void http.listAutoRoutingDecisions(tenantId, 200)
                .then((view) => {
                  if (sequence === decisionSequence.current) setDecisions(view.events);
                })
                .catch((error) => {
                  if (sequence === decisionSequence.current) {
                    onToast({ tone: 'error', message: describeError(error, t) });
                  }
                });
            }}
          >
            {t('autoRouting.decisions.refresh')}
          </button>
        </div>
        <div className="metric-grid">
          {(['weak', 'medium', 'strong'] as const).map((tier) => (
            <Metric
              key={tier}
              label={t(`autoRouting.tier.${tier}`)}
              value={decisions.length
                ? `${Math.round((decisionSummary.tiers[tier] / decisions.length) * 100)}%`
                : '0%'}
            />
          ))}
          <Metric
            label={t('autoRouting.decisions.summary.judgeRate')}
            value={decisionSummary.judgeRuns
              ? `${Math.round((decisionSummary.judgeOk / decisionSummary.judgeRuns) * 100)}%`
              : '0%'}
          />
          <Metric
            label={t('autoRouting.decisions.summary.cacheRate')}
            value={decisionSummary.judgeRuns
              ? `${Math.round((decisionSummary.cacheHits / decisionSummary.judgeRuns) * 100)}%`
              : '0%'}
          />
        </div>
        <p>
          {t('autoRouting.decisions.summary.topReasons')}: {' '}
          {decisionSummary.topReasons.slice(0, 3).map(([reason, count]) =>
            `${reason} ${decisions.length ? Math.round((count / decisions.length) * 100) : 0}%`
          ).join(' · ') || t('common.none')}
        </p>
        <DataTable
          rows={decisions}
          empty={t('autoRouting.decisions.empty')}
          pageSize={50}
          defaultSort={{ key: 'created', dir: 'desc' }}
          getRowKey={(event) => `${event.requestId}:${event.eventType}`}
          columns={[
            { key: 'created', header: t('autoRouting.decisions.created'), render: (event) => event.createdAt ? new Date(event.createdAt).toLocaleString() : '-', sortValue: (event) => event.createdAt ?? '' },
            { key: 'model', header: t('autoRouting.decisions.model'), render: (event) => event.catalogModelId ?? '-' },
            { key: 'tier', header: t('autoRouting.decisions.tier'), render: (event) => event.routeDecision ? `${event.routeDecision.selectedTier} / ${event.routeDecision.executionTier}${event.routeDecision.shadow ? ' · shadow' : ''}` : '-' },
            { key: 'reason', header: t('autoRouting.decisions.reason'), render: (event) => event.routeDecision?.decisionReason ?? '-' },
            { key: 'score', header: t('autoRouting.decisions.score'), render: (event) => event.routeDecision ? `${event.routeDecision.adaptiveScore?.toFixed(3) ?? '-'} / ${event.routeDecision.adaptiveThreshold?.toFixed(3) ?? '-'}` : '-' },
            { key: 'judge', header: t('autoRouting.decisions.judge'), render: (event) => event.routeDecision?.judge ? `${event.routeDecision.judge.taskKind ?? '-'} / ${event.routeDecision.judge.riskLevel ?? '-'} / ${event.routeDecision.judge.difficulty?.toFixed(2) ?? '-'}` : t('common.none') },
            { key: 'cache', header: t('autoRouting.decisions.cache'), render: (event) => event.routeDecision?.judge ? String(event.routeDecision.judge.cacheHit) : t('common.none') },
            { key: 'conversation', header: t('autoRouting.decisions.conversation'), render: (event) => event.conversationId ?? t('common.none') },
          ]}
        />
      </section>

      <section className="panel form-grid">
        <h2>{t('autoRouting.preview')}</h2>
        <p>{t('autoRouting.previewLocalOnly')}</p>
        <label>
          {t('autoRouting.samplePrompt')}
          <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} />
        </label>
        <label>
          {t('autoRouting.taskKind')}
          <select
            value={taskKind ?? ''}
            onChange={(event) => setTaskKind((event.target.value || undefined) as typeof taskKind)}
          >
            <option value="">{t('common.none')}</option>
            {ALL_TASK_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`autoRouting.taskKindLabel.${kind}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('autoRouting.risk')}
          <select value={risk} onChange={(event) => setRisk(event.target.value as typeof risk)}>
            <option value="low">{t('autoRouting.riskLabel.low')}</option>
            <option value="medium">{t('autoRouting.riskLabel.medium')}</option>
            <option value="high">{t('autoRouting.riskLabel.high')}</option>
          </select>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={finalAnswer}
            onChange={(event) => setFinalAnswer(event.target.checked)}
          />
          <span>{t('autoRouting.finalAnswer')}</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={compliance}
            onChange={(event) => setCompliance(event.target.checked)}
          />
          <span>{t('autoRouting.compliance')}</span>
        </label>
        <button disabled={busy || !profile?.catalogItem} onClick={() => void runPreview('local')}>
          {t('autoRouting.runPreview')}
        </button>
        <button disabled={busy || !profile?.catalogItem || !prompt.trim()} onClick={() => void runPreview('judge')}>
          {t('autoRouting.judgePreview.run')}
        </button>
        {preview ? (
          <dl className="criteria-summary preview-result">
            <div><dt>{t('autoRouting.previewField.tier')}</dt><dd>{preview.selectedTier ?? '-'}</dd></div>
            <div><dt>{t('autoRouting.previewField.model')}</dt><dd>{preview.selectedCatalogModelId ?? '-'}</dd></div>
            <div><dt>{t('autoRouting.previewField.thinking')}</dt><dd>{preview.thinkingStrategy ?? '-'}</dd></div>
            <div><dt>{t('autoRouting.previewField.reason')}</dt><dd>{t(`autoRouting.decisionReason.${preview.reason}`, preview.reason)}</dd></div>
            <div><dt>{t('autoRouting.previewField.score')}</dt><dd>{preview.score?.toFixed(3) ?? '-'}</dd></div>
            <div><dt>{t('autoRouting.previewField.threshold')}</dt><dd>{preview.threshold?.toFixed(3) ?? '-'}</dd></div>
            <div><dt>{t('autoRouting.previewField.effective')}</dt><dd>{preview.effective.threshold.toFixed(2)} / {preview.effective.mediumBandThreshold.toFixed(2)}</dd></div>
            {preview.judge ? (
              <>
                <div><dt>{t('autoRouting.judgePreview.outcome')}</dt><dd>{preview.judge.outcome}</dd></div>
                <div><dt>{t('autoRouting.judgePreview.output')}</dt><dd>{preview.judge.output ? `${preview.judge.output.taskKind ?? '-'} / ${preview.judge.output.riskLevel ?? '-'} / ${preview.judge.output.difficulty?.toFixed(2) ?? '-'}` : t('common.none')}</dd></div>
                <div><dt>{t('autoRouting.judgePreview.cost')}</dt><dd>{preview.judge.costCreditMillis?.toFixed(3) ?? t('common.none')}</dd></div>
              </>
            ) : null}
          </dl>
        ) : null}
      </section>
    </div>
  );
}
